import React, { useState, useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators"; // Add Kendo loader

type ResizeDir = "right" | "bottom" | "corner" | null;

interface LogBlock {
  level: "INFO" | "WARNING" | "ERROR" | "UNKNOWN";
  text: string;
}

// --- Parse logs into structured blocks
const parseLogs = (logs: string): LogBlock[] => {
  const lines = logs.split(/\r?\n/);
  const blocks: LogBlock[] = [];
  let current: LogBlock | null = null;

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip lines that start with [RabbitMQProgressReporter][
    if (line.startsWith("[RabbitMQProgressReporter][")) {
      continue;
    }

    if (/^(INFO|WARNING|ERROR)/.test(trimmed)) {
      let level: LogBlock["level"] = "INFO";
      if (trimmed.startsWith("ERROR")) level = "ERROR";
      else if (trimmed.startsWith("WARNING")) level = "WARNING";
      if (current) blocks.push(current);
      current = { level, text: trimmed };
    } else {
      if (current) current.text += "\n" + line;
      else if (trimmed.length > 0) current = { level: "UNKNOWN", text: line };
    }
  }

  if (current) blocks.push(current);
  return blocks;
};

// Use softer SpectroFlow theme colors for log levels
const getColor = (level: LogBlock["level"]) => {
  switch (level) {
    case "ERROR": {
      return "#ffeaea"; // softer red
    }
    case "WARNING": {
      return "#fff9e5"; // softer yellow
    }
    case "INFO": {
      return "#f3fafd"; // softer blue
    }
    default: {
      return "#fafbfc"; // very light gray
    }
  }
};

const LogsDialog = ({
  logContent,
  onClose,
  isResult = false,
  isRunning = false,
  focusLastLineOnOpen = false,
}: {
  logContent: string;
  onClose: () => void;
  isResult?: boolean;
  isRunning?: boolean;
  focusLastLineOnOpen?: boolean;
}) => {
  const dialogReference = useRef<HTMLDivElement | null>(null);
  const logContainerReference = useRef<HTMLDivElement | null>(null);
  const keepBottomFocusReference = useRef(focusLastLineOnOpen);
  const isAutoScrollingReference = useRef(false);

  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [size, setSize] = useState({ width: 1300, height: 700 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [filter, setFilter] = useState<"ALL" | "ERROR" | "WARNING" | "INFO">("ALL");

  const lastStateReference = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
  const positionReference = useRef(position);
  const sizeReference = useRef(size);
  positionReference.current = position;
  sizeReference.current = size;

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDir, setResizeDir] = useState<ResizeDir>(null);

  const offsetReference = useRef({ x: 0, y: 0 });
  const startRectReference = useRef({ mouseX: 0, mouseY: 0, width: 0, height: 0 });

  // Loader state
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const initialX = Math.max((w - size.width) / 2, 20);
    const initialY = Math.max(Math.floor(h * 0.55 - size.height / 2), 20);

    setPosition({
      x: Math.min(initialX, w - 50), // Ensure it's at least partially visible
      y: Math.min(initialY, h - 50),
    });
  }, []);

  useEffect(() => {
    // Simulate loading logs (or wait for logContent to be available)
    if (logContent && logContent.length > 0) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [logContent]);

  const scrollToBottom = () => {
    if (!logContainerReference.current) {
      return;
    }

    isAutoScrollingReference.current = true;
    logContainerReference.current.scrollTop = logContainerReference.current.scrollHeight;
  };

  const handleLogScroll = () => {
    if (isAutoScrollingReference.current) {
      isAutoScrollingReference.current = false;
      return;
    }

    if (!logContainerReference.current) {
      return;
    }

    const distanceFromBottom = logContainerReference.current.scrollHeight - logContainerReference.current.scrollTop - logContainerReference.current.clientHeight;
    if (distanceFromBottom > 8) {
      keepBottomFocusReference.current = false;
    }
  };

  useEffect(() => {
    keepBottomFocusReference.current = focusLastLineOnOpen;
  }, [focusLastLineOnOpen]);

  useEffect(() => {
    if (!focusLastLineOnOpen || loading || !logContent || !keepBottomFocusReference.current) {
      return;
    }

    requestAnimationFrame(scrollToBottom);
  }, [focusLastLineOnOpen, loading, logContent]);

  const handleHeaderMouseDown = (e: React.MouseEvent) => {
    if (isFullscreen) return;
    const rect = dialogReference.current?.getBoundingClientRect();
    if (!rect) return;
    offsetReference.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setIsDragging(true);
  };

  const handleHeaderDoubleClick = () => {
    if (!isFullscreen) {
      lastStateReference.current = { ...positionReference.current, ...sizeReference.current };
      setPosition({ x: 0, y: 0 });
      setSize({ width: window.innerWidth, height: window.innerHeight });
      setIsFullscreen(true);
    } else if (lastStateReference.current) {
      setPosition({
        x: lastStateReference.current.x,
        y: lastStateReference.current.y,
      });
      setSize({
        width: lastStateReference.current.width,
        height: lastStateReference.current.height,
      });
      setIsFullscreen(false);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - offsetReference.current.x,
        y: e.clientY - offsetReference.current.y,
      });
    } else if (isResizing && resizeDir && !isFullscreen) {
      const dx = e.clientX - startRectReference.current.mouseX;
      const dy = e.clientY - startRectReference.current.mouseY;
      switch (resizeDir) {
        case "right": {
          setSize({
            width: Math.max(500, startRectReference.current.width + dx),
            height: sizeReference.current.height,
          });

          break;
        }
        case "bottom": {
          setSize({
            width: sizeReference.current.width,
            height: Math.max(300, startRectReference.current.height + dy),
          });

          break;
        }
        case "corner": {
          setSize({
            width: Math.max(500, startRectReference.current.width + dx),
            height: Math.max(300, startRectReference.current.height + dy),
          });

          break;
        }
        // No default
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  const handleResizeMouseDown = (dir: ResizeDir, e: React.MouseEvent) => {
    if (isFullscreen) return;
    e.stopPropagation();
    const rect = dialogReference.current?.getBoundingClientRect();
    if (!rect) return;
    startRectReference.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      width: rect.width,
      height: rect.height,
    };
    setIsResizing(true);
    setResizeDir(dir);
  };

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  });

  // --- Parse logs into structured blocks
  const logBlocks = parseLogs(logContent || "");
  const counts = {
    ERROR: logBlocks.filter((b) => b.level === "ERROR").length,
    WARNING: logBlocks.filter((b) => b.level === "WARNING").length,
    INFO: logBlocks.filter((b) => b.level === "INFO").length,
  };

  const filteredLogs = filter === "ALL" ? logBlocks : logBlocks.filter((b) => b.level === filter);

  const toggleFilter = (type: "ERROR" | "WARNING" | "INFO") => {
    setFilter((previous) => (previous === type ? "ALL" : type));
  };

  const renderLogs = () => {
    if ((filter === "ERROR" && counts.ERROR === 0) || (filter === "WARNING" && counts.WARNING === 0) || (filter === "INFO" && counts.INFO === 0)) {
      let label = "No info logs";
      if (filter === "ERROR") label = "No error logs";
      else if (filter === "WARNING") label = "No warning logs";
      return (
        <div
          style={{
            fontStyle: "italic",
            color: "#777",
            padding: "10px",
            textAlign: "center",
          }}
        >
          {label}
        </div>
      );
    }

    return filteredLogs.map((block, index) => (
      <div
        key={index}
        style={{
          backgroundColor: getColor(block.level),
          padding: "2px 6px",
          marginBottom: "2px",
          borderRadius: 3,
          fontSize: "13px",
          lineHeight: "1.2",
          userSelect: "text",
          cursor: "text",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {block.text}
      </div>
    ));
  };

  return (
    <div
      ref={dialogReference}
      style={{
        position: "fixed",
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        backgroundColor: "white",
        border: "1px solid #ccc",
        borderRadius: 8,
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        zIndex: 2000,
      }}
    >
      {/* Header */}
      <div
        onMouseDown={handleHeaderMouseDown}
        onDoubleClick={handleHeaderDoubleClick}
        style={{
          cursor: isFullscreen ? "default" : "move",
          padding: "4px 10px",
          backgroundColor: "#f0f0f0",
          borderBottom: "1px solid #ccc",
          flex: "0 0 auto",
          userSelect: "none",
          fontSize: "15px",
        }}
      >
        <span style={{ fontWeight: 600 }}>{isResult ? "Results" : <>Logs {isFullscreen && "(Fullscreen)"}</>}</span>
      </div>

      {/* Logs content */}
      <div
        ref={logContainerReference}
        onScroll={handleLogScroll}
        style={{
          flex: "1 1 auto",
          overflow: "auto",
          padding: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: loading ? "center" : "stretch",
          justifyContent: loading ? "center" : "flex-start",
        }}
      >
        {loading ? (
          <Loader type="infinite-spinner" size="large" />
        ) : (
          // wrap logs so they take full width when not loading and stack vertically
          <div style={{ width: "100%" }}>{renderLogs()}</div>
        )}
      </div>

      {/* Footer buttons */}
      <div
        style={{
          borderTop: "1px solid #ccc",
          padding: "8px",
          background: "#fcfcfc", // less intense background
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          flex: "0 0 auto",
          position: "relative",
        }}
      >
        {!isResult && (
          <div style={{ display: "flex", gap: 8, position: "absolute", left: 8, bottom: 8 }}>
            <Button
              onClick={() => toggleFilter("ERROR")}
              themeColor={filter === "ERROR" ? "error" : "base"}
              style={{
                background: filter === "ERROR" ? "#ffeaea" : undefined,
                color: filter === "ERROR" ? "#b71c1c" : undefined,
              }}
            >
              {filter === "ERROR" ? "Show All" : `Show Errors (${counts.ERROR})`}
            </Button>
            <Button
              onClick={() => toggleFilter("WARNING")}
              themeColor={filter === "WARNING" ? "warning" : "base"}
              style={{
                background: filter === "WARNING" ? "#fff9e5" : undefined,
                color: filter === "WARNING" ? "#b26a00" : undefined,
              }}
            >
              {filter === "WARNING" ? "Show All" : `Show Warnings (${counts.WARNING})`}
            </Button>
            <Button
              onClick={() => toggleFilter("INFO")}
              themeColor={filter === "INFO" ? "info" : "base"}
              style={{
                background: filter === "INFO" ? "#e3f2fd" : undefined, // blue for info
                color: filter === "INFO" ? "#1565c0" : undefined,
              }}
            >
              {filter === "INFO" ? "Show All" : `Show Info (${counts.INFO})`}
            </Button>
            {isRunning && (
              <Button themeColor="base" onClick={scrollToBottom}>
                Go to last line
              </Button>
            )}
          </div>
        )}
        <Button themeColor="error" onClick={onClose}>
          Close
        </Button>
      </div>

      {/* Resize handles */}
      {!isFullscreen && (
        <>
          <div
            onMouseDown={(e) => handleResizeMouseDown("right", e)}
            style={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: 6,
              cursor: "ew-resize",
            }}
          />
          <div
            onMouseDown={(e) => handleResizeMouseDown("bottom", e)}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 6,
              cursor: "ns-resize",
            }}
          />
          <div
            onMouseDown={(e) => handleResizeMouseDown("corner", e)}
            style={{
              position: "absolute",
              right: 0,
              bottom: 0,
              width: 14,
              height: 14,
              cursor: "nwse-resize",
            }}
          />
        </>
      )}
    </div>
  );
};

export default LogsDialog;
